import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles, Tooltip, IconButton, Typography } from '@material-ui/core';
import { FindInPage } from '@material-ui/icons';
import { Redirect } from 'react-router-dom';
import { hasPermission, createHistory } from '../../sc-utilities';
import SKUMappingList from './SKUMappingList';
import { formatProductTypes } from '../../sc-admin/SystemCertificate/utils';

const styles = (theme) => ({
    root: {
        width: `100%`,
        height: 'auto',
        paddingLeft: theme.spacing(-1),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%',
        },
    },
});

const SKUMapping = (props) => {
    const { classes, columns, translations, permissions } = props;

    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, skuMappingID: null, redirectPath: '' });
    const redirectHandler = (path, skuMappingID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, skuMappingID: skuMappingID, redirectPath: path });
    };

    let visible = columns && columns['SKUMapping'];
    const tableColumns = useMemo(() => {
        return [
            {
                title: translations['action'] || 'Action',
                field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: (rowData) => (
                    <>
                        {hasPermission(permissions, 'sku_mapping_load') ? (
                            <Tooltip title={translations['sku_mapping_details'] || 'SKU mapping details'}>
                                <IconButton
                                    id={`edit_sku_mapping_${rowData.SKUMappingID}`}
                                    component="div"
                                    onClick={(event) =>
                                        redirectHandler(
                                            `/settings/sku-mapping/edit?skuMappingID=${rowData.SKUMappingID}`
                                        )
                                    }
                                >
                                    <FindInPage />
                                </IconButton>
                            </Tooltip>
                        ) : null}
                    </>
                ),
            },
            {
                title: translations['product_type'] || 'Product type',
                field: 'ProductType',
                hidden: visible ? visible['ProductType'] : false,
                lookup: {
                    MobileProduct: translations['mobile_product'] || 'Mobile',
                    PCProduct: translations['pc_product'] || 'Workplace',
                    StorageProduct: translations['storage_product'] || 'Single disk-drives',
                    WatchProduct: translations['watch'] || 'Watch',
                    HeadphoneProduct: translations['headphone'] || 'Headphone',
                },
                defaultFilter: ['MobileProduct', 'PCProduct', 'StorageProduct', 'WatchProduct', 'HeadphoneProduct'],
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.ProductType
                                ? formatProductTypes(rowData.ProductType, translations)
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['vendor'] || 'Vendor',
                field: 'ProductType',
                hidden: visible ? visible['Vendor'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Vendor ? rowData.Vendor : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['model'] || 'Model',
                field: 'Model',
                hidden: visible ? visible['Model'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Model ? rowData.Model : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['product_identifier'] || 'Product identifier',
                field: 'ProductIdentifier',
                hidden: visible ? visible['ProductIdentifier'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.ProductIdentifier
                                ? rowData.ProductIdentifier
                                : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['storage'] || 'Storage',
                field: 'Storage',
                hidden: visible ? visible['Storage'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Storage ? rowData.Storage : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['memory'] || 'Memory',
                field: 'Memory',
                hidden: visible ? visible['Memory'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Memory ? rowData.Memory : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['cpu'] || 'CPU',
                field: 'CPU',
                hidden: visible ? visible['CPU'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.CPU ? rowData.CPU : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['gpu'] || 'GPU',
                field: 'GPU',
                hidden: visible ? visible['GPU'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.GPU ? rowData.GPU : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['grading'] || 'Grading',
                field: 'Grading',
                hidden: visible ? visible['Grading'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Grading ? rowData.Grading : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['color'] || 'Color',
                field: 'Color',
                hidden: visible ? visible['Color'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Color ? rowData.Color : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['carrier'] || 'Carrier',
                field: 'Carrier',
                hidden: visible ? visible['Carrier'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.Carrier ? rowData.Carrier : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
            {
                title: translations['sku'] || 'SKU',
                field: 'SKU',
                hidden: visible ? visible['SKU'] : false,
                filtering: true,
                render: (rowData) => {
                    return (
                        <Typography variant={'caption'}>
                            {rowData.SKU ? rowData.SKU : translations['short_not_assigned'] || 'n/a'}
                        </Typography>
                    );
                },
            },
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classes, translations, visible]);
    if (!hasPermission(permissions, 'sku_mapping_load')) {
        tableColumns.shift();
    }
    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    return (
        <div className={classes.root}>
            {hasPermission(permissions, 'sku_mapping_list') ? (
                <SKUMappingList tableColumns={tableColumns} redirectHandler={redirectHandler} />
            ) : null}
        </div>
    );
};

const mapStateToProps = (state) => {
    return {
        translations:
            state.translate && state.translate.translations
                ? state.translate.translations.activeTranslations || {}
                : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        permissions: state?.core?.login?.permissions || [],
    };
};

export default connect(mapStateToProps, null)(withStyles(styles)(SKUMapping));
